import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import safeToast from "utils/safeToast";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";

// MUI Components
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  Badge,
  Tooltip,
  Card,
  CardContent,
  Grid,
  alpha,
  useMediaQuery,
  useTheme,
  Popover,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import SendIcon from "@mui/icons-material/Send";
import ChatIcon from "@mui/icons-material/Chat";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import VideocamIcon from "@mui/icons-material/Videocam";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import CloseIcon from "@mui/icons-material/Close";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ReplyIcon from "@mui/icons-material/Reply";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";

// Custom Components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Services & Hooks
import privateChatService from "API/privateChatService";
import { useSignalR } from "hooks/useSignalR";
import signalRManager from "services/signalRManager";
import { getFullUrl } from "config/api";
import { decryptText } from "config/encryption";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};

const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Format date header (Today, Yesterday, or date)
const formatDateHeader = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const messageDate = new Date(date);
  messageDate.setHours(0, 0, 0, 0);
  
  if (messageDate.getTime() === today.getTime()) {
    return "Today";
  } else if (messageDate.getTime() === yesterday.getTime()) {
    return "Yesterday";
  } else {
    return messageDate.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: messageDate.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
    });
  }
};

// Check if two dates are on the same day
const isSameDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);
  return d1.getTime() === d2.getTime();
};

// Typing animation keyframes
const typingAnimation = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  30% {
    transform: translateY(-8px);
    opacity: 1;
  }
`;

const PrivateChat = ({ otherStudentId, otherStudentName, otherProfileImageUrl, onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editMessageText, setEditMessageText] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");

  // Handle video stream assignment when camera opens
  useEffect(() => {
    if (cameraOpen && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(err => {
        console.error("Error playing video:", err);
      });
    }
    
    return () => {
      // Cleanup: stop stream when component unmounts or camera closes
      if (stream && !cameraOpen) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraOpen, stream]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioChunks, setAudioChunks] = useState([]);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [audioPreviewBlob, setAudioPreviewBlob] = useState(null);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [emojiPickerAnchor, setEmojiPickerAnchor] = useState(null);
  const [currentStudentId, setCurrentStudentId] = useState(null);
  
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioRefs = useRef({});
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  const { connection, isConnected, onlineUsers } = useSignalR();

  // Get current user ID
  useEffect(() => {
    const userDetails = localStorage.getItem("UserLogDetails");
    if (userDetails) {
      try {
        const decrypted = decryptText(userDetails);
        const { StudentId } = JSON.parse(decrypted);
        setCurrentStudentId(StudentId);
      } catch (error) {
        console.error("Error parsing user details:", error);
      }
    }
  }, []);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Load chat history
  const loadChatHistory = useCallback(async (page = 1, append = false) => {
    if (!otherStudentId) return;
    
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await privateChatService.getChatHistory(otherStudentId, page, 50);
      
      if (append) {
        setChatHistory((prev) => [...(response.messages || []), ...prev]);
      } else {
        setChatHistory(response.messages || []);
      }
      
      setTotalMessages(response.totalCount || 0);
      setPageNumber(page);

      if (!append) {
        setTimeout(() => scrollToBottom(), 100);
      }
    } catch (error) {
      try {
        safeToast.error("Failed to load chat history");
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      console.error("Error loading chat history:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [otherStudentId]);

  // Initial load
  useEffect(() => {
    if (!otherStudentId) return;
    
    const userDetails = localStorage.getItem("UserLogDetails");
    if (!userDetails) {
      safeToast.warn("Please login to access chat");
      if (onClose) onClose();
      return;
    }

    loadChatHistory();
    
    // Mark all messages as read when chat opens
    const markAllRead = async () => {
      try {
        await privateChatService.markAllAsRead(otherStudentId);
      } catch (error) {
        console.error("Error marking all messages as read:", error);
      }
    };
    
    // Delay marking as read slightly to ensure chat is loaded
    setTimeout(() => {
      markAllRead();
    }, 500);
  }, [otherStudentId, loadChatHistory, onClose]);

  // SignalR event handlers for private chat
  useEffect(() => {
    if (!connection || !isConnected || !otherStudentId) return;

    const handleReceivePrivateMessage = (privateMessage) => {
      if (privateMessage.fromStudentId === otherStudentId || privateMessage.toStudentId === otherStudentId) {
        setChatHistory((prev) => {
          if (prev.some((m) => m.messageId === privateMessage.messageId)) {
            return prev;
          }
          return [...prev, privateMessage];
        });
        setTimeout(() => scrollToBottom(), 100);
        
        // Mark as read
        if (privateMessage.toStudentId === currentStudentId && !privateMessage.isRead) {
          privateChatService.markAsRead(privateMessage.messageId).catch(console.error);
        }
      }
    };

    const handlePrivateMessageSent = (privateMessage) => {
      setChatHistory((prev) => {
        if (prev.some((m) => m.messageId === privateMessage.messageId)) {
          return prev;
        }
        return [...prev, privateMessage];
      });
      setTimeout(() => scrollToBottom(), 100);
    };

    const handlePrivateMessageDelivered = (deliveredMessage) => {
      setChatHistory((prev) =>
        prev.map((m) =>
          m.messageId === deliveredMessage.messageId
            ? { ...m, deliveredDateTime: deliveredMessage.deliveredDateTime, DeliveredDateTime: deliveredMessage.deliveredDateTime || deliveredMessage.DeliveredDateTime }
            : m
        )
      );
    };

    const handlePrivateMessageRead = (readMessage) => {
      setChatHistory((prev) =>
        prev.map((m) =>
          m.messageId === readMessage.messageId
            ? { ...m, isRead: true, IsRead: true, readDateTime: readMessage.readDateTime || readMessage.ReadDateTime }
            : m
        )
      );
    };

    const handlePrivateMessageDeleted = (data) => {
      setChatHistory((prev) => prev.filter((m) => m.messageId !== data.messageId));
    };

    const handlePrivateMessageUpdated = (updatedMessage) => {
      setChatHistory((prev) =>
        prev.map((m) =>
          m.messageId === updatedMessage.messageId ? updatedMessage : m
        )
      );
    };

    const handlePrivateTyping = (data) => {
      if (data.fromStudentId === otherStudentId) {
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 3000);
      }
    };

    const handlePrivateTypingStarted = (data) => {
      if (data.fromStudentId === otherStudentId) {
        setIsTyping(true);
      }
    };

    const handlePrivateTypingStopped = (data) => {
      if (data.fromStudentId === otherStudentId) {
        setIsTyping(false);
      }
    };

    connection.on("ReceivePrivateMessage", handleReceivePrivateMessage);
    connection.on("PrivateMessageSent", handlePrivateMessageSent);
    connection.on("PrivateMessageDelivered", handlePrivateMessageDelivered);
    connection.on("PrivateMessageRead", handlePrivateMessageRead);
    connection.on("PrivateMessageDeleted", handlePrivateMessageDeleted);
    connection.on("PrivateMessageUpdated", handlePrivateMessageUpdated);
    connection.on("PrivateTyping", handlePrivateTyping);
    connection.on("PrivateTypingStarted", handlePrivateTypingStarted);
    connection.on("PrivateTypingStopped", handlePrivateTypingStopped);

    return () => {
      connection.off("ReceivePrivateMessage", handleReceivePrivateMessage);
      connection.off("PrivateMessageSent", handlePrivateMessageSent);
      connection.off("PrivateMessageDelivered", handlePrivateMessageDelivered);
      connection.off("PrivateMessageRead", handlePrivateMessageRead);
      connection.off("PrivateMessageDeleted", handlePrivateMessageDeleted);
      connection.off("PrivateMessageUpdated", handlePrivateMessageUpdated);
      connection.off("PrivateTyping", handlePrivateTyping);
      connection.off("PrivateTypingStarted", handlePrivateTypingStarted);
      connection.off("PrivateTypingStopped", handlePrivateTypingStopped);
    };
  }, [connection, isConnected, otherStudentId, currentStudentId]);

  // Auto-scroll when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, isTyping]);

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    if (!isConnected || !otherStudentId) return;
    
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    signalRManager.startPrivateTyping(otherStudentId);
    
    const timeout = setTimeout(() => {
      signalRManager.stopPrivateTyping(otherStudentId);
      setTypingTimeout(null);
    }, 3000);
    
    setTypingTimeout(timeout);
  }, [isConnected, otherStudentId, typingTimeout]);

  // Handle send message
  const handleSendMessage = async () => {
    // Check if message has content (handles emojis correctly - emojis have length > 0)
    const hasMessageContent = message && message.trim().length > 0;
    if ((!hasMessageContent && !selectedImage && !selectedVideo && !audioPreviewBlob) || sending || !isConnected || !otherStudentId) return;

    // Clear typing timeout when sending
    if (typingTimeout) {
      clearTimeout(typingTimeout);
      setTypingTimeout(null);
    }
    signalRManager.stopPrivateTyping(otherStudentId);

    setSending(true);
    let imageUrl = null;
    let videoUrl = null;
    let audioUrl = null;

    try {
      // Upload image if selected
      if (selectedImage) {
        setUploadingImage(true);
        const uploadResult = await privateChatService.uploadImage(selectedImage);
        imageUrl = uploadResult.imageUrl;
        setUploadingImage(false);
      }

      // Upload video if selected
      if (selectedVideo) {
        setUploadingVideo(true);
        const uploadResult = await privateChatService.uploadVideo(selectedVideo);
        videoUrl = uploadResult.videoUrl;
        setUploadingVideo(false);
      }

      // Upload audio if recorded (use preview blob if available)
      if (audioPreviewBlob) {
        console.log("Uploading audio preview blob, size:", audioPreviewBlob.size);
        try {
          const uploadResult = await privateChatService.uploadAudio(audioPreviewBlob);
          console.log("Audio upload result:", uploadResult);
          audioUrl = uploadResult.audioUrl;
          setAudioPreview(null);
          setAudioPreviewBlob(null);
        } catch (uploadError) {
          console.error("Error uploading audio:", uploadError);
          try {
            safeToast.error("Failed to upload audio message");
          } catch (toastErr) {
            console.error("Toast error:", toastErr);
          }
          setSending(false);
          return;
        }
      } else if (audioChunks.length > 0) {
        console.log("Uploading audio from chunks, count:", audioChunks.length);
        try {
          const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
          const uploadResult = await privateChatService.uploadAudio(audioBlob);
          console.log("Audio upload result:", uploadResult);
          audioUrl = uploadResult.audioUrl;
          setAudioChunks([]);
        } catch (uploadError) {
          console.error("Error uploading audio:", uploadError);
          try {
            safeToast.error("Failed to upload audio message");
          } catch (toastErr) {
            console.error("Toast error:", toastErr);
          }
          setSending(false);
          return;
        }
      }
      setMediaRecorder(null);

      // Final validation: ensure we have at least one content field
      // Handle emojis correctly - emojis are valid content even if they're just emoji characters
      let trimmedMessage = message ? message.trim() : "";
      const hasMessage = trimmedMessage.length > 0;
      const hasImage = imageUrl && imageUrl.trim().length > 0;
      const hasVideo = videoUrl && videoUrl.trim().length > 0;
      const hasAudio = audioUrl && audioUrl.trim().length > 0;

      if (!hasMessage && !hasImage && !hasVideo && !hasAudio) {
        safeToast.error("Please add a message, image, video, or audio before sending");
        setSending(false);
        return;
      }

      // If message contains only emojis (no regular text), add a dot to ensure it's recognized as valid content
      if (hasMessage && trimmedMessage) {
        // Check if message contains only emojis and whitespace (no regular text characters)
        // Remove all emojis and whitespace, if nothing remains, it's emoji-only
        const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{200D}]|[\u{FE0F}]/gu;
        const textWithoutEmojis = trimmedMessage.replace(emojiRegex, '').replace(/\s/g, '');
        
        // If after removing emojis and whitespace, nothing remains, it's emoji-only
        if (textWithoutEmojis.length === 0 && trimmedMessage.length > 0) {
          // Add a dot after emoji-only messages to ensure backend validation passes
          trimmedMessage = trimmedMessage + ".";
        }
      }

      // Send message - preserve emojis by sending trimmed message or null if empty
      const replyToMessageId = replyToMessage?.messageId || null;
      const messageToSend = hasMessage ? trimmedMessage : null;
      
      if (isConnected && connection) {
        await signalRManager.sendPrivateMessage(
          otherStudentId,
          messageToSend,
          imageUrl,
          videoUrl,
          audioUrl,
          replyToMessageId
        );
      } else {
        await privateChatService.sendMessage(
          otherStudentId,
          messageToSend,
          imageUrl,
          videoUrl,
          audioUrl,
          replyToMessageId
        );
      }

      // Clear inputs
      setMessage("");
      setSelectedImage(null);
      setSelectedVideo(null);
      setImagePreview(null);
      setVideoPreview(null);
      setAudioPreview(null);
      setAudioPreviewBlob(null);
      setReplyToMessage(null);
      setAudioChunks([]);
      inputRef.current?.focus();
    } catch (error) {
      try {
        safeToast.error("Failed to send message");
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      console.error("Error sending message:", error);
      setUploadingImage(false);
      setUploadingVideo(false);
    } finally {
      setSending(false);
    }
  };

  // Handle image select
  const handleImageSelect = (event) => {
    const file = event.target.files?.[0];
    if (file && file instanceof File) {
      if (file.type.startsWith('image/')) {
        setSelectedImage(file);
        setSelectedVideo(null);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.onerror = (error) => {
          console.error("FileReader error:", error);
          try {
            safeToast.error("Failed to create image preview");
          } catch (toastErr) {
            console.error("Toast error:", toastErr);
          }
        };
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('video/')) {
        setSelectedVideo(file);
        setSelectedImage(null);
        const reader = new FileReader();
        reader.onloadend = () => {
          setVideoPreview(reader.result);
        };
        reader.onerror = (error) => {
          console.error("FileReader error:", error);
          try {
            safeToast.error("Failed to create video preview");
          } catch (toastErr) {
            console.error("Toast error:", toastErr);
          }
        };
        reader.readAsDataURL(file);
      }
    } else {
      console.error("Invalid file selected:", file);
      // Reset input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Handle remove image/video
  const handleRemoveImage = () => {
    setSelectedImage(null);
    setSelectedVideo(null);
    setImagePreview(null);
    setVideoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  // Handle camera open
  const handleOpenCamera = async () => {
    try {
      setCameraOpen(true);
      setVideoReady(false); // Reset ready state
      
      // Wait a bit for dialog to render
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facingMode },
        audio: false
      });
      setStream(stream);
      
      // Wait for video element to be available
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(err => {
          console.error("Error playing video:", err);
        });
      } else {
        // If video element not ready, try again after a short delay
        setTimeout(() => {
          if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch(err => {
              console.error("Error playing video:", err);
            });
          }
        }, 200);
      }
    } catch (error) {
      setCameraOpen(false);
      try {
        safeToast.error("Failed to access camera: " + (error.message || "Unknown error"));
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      console.error("Error accessing camera:", error);
    }
  };

  // Handle camera close
  const handleCloseCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraOpen(false);
    setVideoReady(false);
  };

  // Handle switch camera
  const handleSwitchCamera = async () => {
    handleCloseCamera();
    setFacingMode(facingMode === "user" ? "environment" : "user");
    setTimeout(() => handleOpenCamera(), 100);
  };

  // Handle capture photo
  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      console.error("Video or canvas ref not available");
      try {
        safeToast.error("Camera not ready");
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      return;
    }

    const canvas = canvasRef.current;
    const video = videoRef.current;

    // Check if video is ready and has valid dimensions
    if (!videoReady || !video.videoWidth || !video.videoHeight || video.videoWidth === 0 || video.videoHeight === 0) {
      console.error("Video not ready or invalid dimensions:", {
        videoReady,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        readyState: video.readyState
      });
      try {
        safeToast.error("Please wait for camera to be ready");
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      return;
    }

    try {
      // Set canvas dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw video frame to canvas
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error("Failed to get canvas context");
      }
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob && blob instanceof Blob) {
          // Convert Blob to File for consistency
          const imageFile = new File([blob], "camera-photo.jpg", { type: "image/jpeg" });
          setSelectedImage(imageFile);
          const reader = new FileReader();
          reader.onloadend = () => {
            setImagePreview(reader.result);
          };
          reader.onerror = (error) => {
            console.error("FileReader error:", error);
            try {
              safeToast.error("Failed to create image preview");
            } catch (toastErr) {
              console.error("Toast error:", toastErr);
            }
          };
          reader.readAsDataURL(blob);
          handleCloseCamera();
        } else {
          // Fallback: try using canvas.toDataURL
          try {
            const dataURL = canvas.toDataURL('image/jpeg', 0.95);
            if (dataURL && dataURL !== 'data:,') {
              setImagePreview(dataURL);
              // Convert dataURL to File
              const byteString = atob(dataURL.split(',')[1]);
              const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
              const ab = new ArrayBuffer(byteString.length);
              const ia = new Uint8Array(ab);
              for (let i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
              }
              const imageFile = new File([ab], "camera-photo.jpg", { type: mimeString });
              setSelectedImage(imageFile);
              handleCloseCamera();
            } else {
              throw new Error("Failed to generate data URL");
            }
          } catch (fallbackError) {
            console.error("Failed to create blob from canvas:", fallbackError);
            try {
              safeToast.error("Failed to capture photo");
            } catch (toastErr) {
              console.error("Toast error:", toastErr);
            }
          }
        }
      }, 'image/jpeg', 0.95);
    } catch (error) {
      console.error("Error capturing photo:", error);
      try {
        safeToast.error("Failed to capture photo: " + (error.message || "Unknown error"));
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
    }
  };

  // Handle start recording
  const handleStartRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        try {
          safeToast.error("Microphone access is not supported in your browser");
        } catch (toastErr) {
          console.error("Toast error:", toastErr);
        }
        return;
      }

      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioRecorderRef.current = audioStream;

      const recorder = new MediaRecorder(audioStream);
      const chunks = [];

      // Store chunks in a ref to ensure they're accessible
      const chunksRef = { current: chunks };

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
          console.log("Audio chunk received:", event.data.size, "bytes");
        }
      };

      recorder.onstop = async () => {
        try {
          // Small delay to ensure all data is collected
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Ensure all chunks are collected
          if (chunksRef.current.length === 0) {
            console.warn("No audio chunks collected");
            setIsRecording(false);
            setRecordingTime(0);
            audioStream.getTracks().forEach((track) => track.stop());
            if (recordingTimerRef.current) {
              clearInterval(recordingTimerRef.current);
              recordingTimerRef.current = null;
            }
            try {
              safeToast.error("No audio data recorded. Please try again.");
            } catch (toastErr) {
              console.error("Toast error:", toastErr);
            }
            return;
          }

          console.log("Total chunks collected:", chunksRef.current.length);
          const audioBlob = new Blob(chunksRef.current, { type: "audio/webm" });
          
          console.log("Audio blob created:", audioBlob.size, "bytes");
          
          // Create File object from Blob (required for upload with filename)
          const audioFile = new File([audioBlob], "voice-message.webm", { type: "audio/webm" });
          
          // Show preview
          const reader = new FileReader();
          reader.onloadend = () => {
            setAudioPreview(reader.result);
            setAudioPreviewBlob(audioFile); // Store File object instead of Blob
            console.log("Audio preview set");
          };
          reader.onerror = (error) => {
            console.error("FileReader error:", error);
            try {
              safeToast.error("Failed to create audio preview");
            } catch (toastErr) {
              console.error("Toast error:", toastErr);
            }
          };
          reader.readAsDataURL(audioBlob);

          // Stop all tracks
          audioStream.getTracks().forEach((track) => track.stop());
          setAudioChunks([]);
          setIsRecording(false);
          setRecordingTime(0);
          if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
          }
        } catch (error) {
          console.error("Error in recorder.onstop:", error);
          try {
            safeToast.error("Failed to process recording");
          } catch (toastErr) {
            console.error("Toast error:", toastErr);
          }
          setIsRecording(false);
          setRecordingTime(0);
          audioStream.getTracks().forEach((track) => track.stop());
        }
      };

      // Start recording with timeslice to ensure data is collected
      recorder.start(100); // Collect data every 100ms
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
      setAudioChunks([]); // Clear previous chunks
      setAudioPreview(null); // Clear previous preview
      setAudioPreviewBlob(null); // Clear previous preview blob

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      try {
        safeToast.error("Failed to access microphone. Please check permissions.");
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
    }
  };

  // Handle stop recording
  const handleStopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  // Handle cancel recording
  const handleCancelRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }
    if (audioRecorderRef.current) {
      audioRecorderRef.current.getTracks().forEach((track) => track.stop());
    }
    setAudioChunks([]);
    setIsRecording(false);
    setRecordingTime(0);
    setAudioPreview(null);
    setAudioPreviewBlob(null);
    setMediaRecorder(null);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  // Handle toggle audio playback
  const handleToggleAudio = (messageId, audioUrl) => {
    const audioElement = audioRefs.current[messageId];
    if (!audioElement) return;

    if (playingAudioId === messageId) {
      audioElement.pause();
      setPlayingAudioId(null);
    } else {
      if (playingAudioId) {
        const prevAudio = audioRefs.current[playingAudioId];
        if (prevAudio) prevAudio.pause();
      }
      audioElement.play();
      setPlayingAudioId(messageId);
      
      audioElement.onended = () => {
        setPlayingAudioId(null);
      };
    }
  };

  // Handle menu open
  const handleMenuOpen = (event, message) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedMessage(message);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // Handle edit message
  const handleEditClick = () => {
    if (selectedMessage && !selectedMessage.imageUrl && !selectedMessage.videoUrl && !selectedMessage.audioUrl) {
      setEditMessageText(selectedMessage.message || "");
      setEditDialogOpen(true);
      setMenuAnchor(null);
    }
  };

  // Handle delete message
  const handleDeleteClick = () => {
    setMenuAnchor(null);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!selectedMessage || deleting) return;

    setDeleting(true);
    
    // Store message data before clearing
    const messageToDelete = selectedMessage;
    const messageIdToDelete = selectedMessage.messageId;
    
    try {
      // Remove from UI first (optimistic update)
      setChatHistory((prev) => prev.filter((m) => m.messageId !== messageIdToDelete));
      setSelectedMessage(null);
      setDeleteDialogOpen(false);
      
      // Try to delete via API
      try {
        await privateChatService.deleteMessage(messageIdToDelete);
      } catch (apiError) {
        // Silently handle errors - don't show toast notifications
        // If API call fails, try to rollback by re-adding message to UI
        const errorMessage = apiError.response?.data?.error || apiError.message || "";
        if (!errorMessage.toLowerCase().includes("message not found") && 
            !errorMessage.toLowerCase().includes("not found")) {
          // Re-add message to UI if deletion failed (rollback)
          setChatHistory((prev) => {
            const messageExists = prev.some(m => m.messageId === messageIdToDelete);
            if (!messageExists && messageToDelete) {
              return [...prev, messageToDelete];
            }
            return prev;
          });
        }
        console.error("Error deleting message:", apiError);
      }
      
      // Try to notify via SignalR if connected
      if (isConnected && connection) {
        try {
          await signalRManager.deletePrivateMessage(messageIdToDelete);
        } catch (signalRError) {
          console.error("Error notifying via SignalR:", signalRError);
          // Don't show error for SignalR failures as message is already deleted
        }
      }
    } catch (error) {
      console.error("Unexpected error deleting message:", error);
    } finally {
      setDeleting(false);
    }
  };

  // Confirm edit
  const handleConfirmEdit = async () => {
    if (!selectedMessage || !editMessageText.trim() || editing) return;

    setEditing(true);
    
    try {
      await privateChatService.updateMessage(selectedMessage.messageId, editMessageText.trim());
      
      if (isConnected && connection) {
        await signalRManager.updatePrivateMessage(selectedMessage.messageId, editMessageText.trim());
      }
      
      setChatHistory((prev) =>
        prev.map((m) =>
          m.messageId === selectedMessage.messageId
            ? { ...m, message: editMessageText.trim(), isEdited: true, editedDateTime: new Date().toISOString() }
            : m
        )
      );
      
      setSelectedMessage(null);
      setEditDialogOpen(false);
      setEditMessageText("");
    } catch (error) {
      try {
        safeToast.error(error.response?.data?.error || "Failed to update message");
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      console.error("Error updating message:", error);
    } finally {
      setEditing(false);
    }
  };

  // Handle reply to message
  const handleReplyClick = (message) => {
    setReplyToMessage(message);
    inputRef.current?.focus();
  };

  // Handle emoji select
  const handleEmojiSelect = (emoji) => {
    setMessage((prev) => prev + emoji.native);
    setEmojiPickerAnchor(null);
  };

  // Format recording time
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Check if the other user is online
  const isOtherUserOnline = useCallback(() => {
    if (!onlineUsers || onlineUsers.length === 0 || !otherStudentId) return false;
    return onlineUsers.some(user => {
      // Handle both camelCase and PascalCase property names
      const userId = user.studentId || user.StudentId;
      const userIdNum = typeof userId === 'string' ? parseInt(userId) : userId;
      const targetId = typeof otherStudentId === 'string' ? parseInt(otherStudentId) : otherStudentId;
      return userIdNum === targetId;
    });
  }, [onlineUsers, otherStudentId]);

  const friendIsOnline = isOtherUserOnline();

  // Helper function to get tick icon based on message status (WhatsApp-style)
  const getTickIcon = (message) => {
    if (!message || message.fromStudentId !== currentStudentId) return null;
    
    const isRead = message.isRead || message.IsRead;
    const deliveredDateTime = message.deliveredDateTime || message.DeliveredDateTime;
    
    if (isRead) {
      // Double green tick - message is read
      return (
        <DoneAllIcon
          sx={{
            fontSize: 16,
            color: "success.main",
            ml: 0.5,
          }}
        />
      );
    } else if (deliveredDateTime) {
      // Double grey tick - message is delivered but not read
      return (
        <DoneAllIcon
          sx={{
            fontSize: 16,
            color: "text.secondary",
            ml: 0.5,
          }}
        />
      );
    } else {
      // Single grey tick - message is sent but not delivered
      return (
        <DoneIcon
          sx={{
            fontSize: 16,
            color: "text.secondary",
            ml: 0.5,
          }}
        />
      );
    }
  };

  if (!otherStudentId) {
    return (
      <Box p={3}>
        <Typography>No chat selected</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderRadius: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {onClose && (
          <IconButton onClick={onClose} size="small">
            <ArrowBackIcon />
          </IconButton>
        )}
        <Avatar
          src={
            otherProfileImageUrl
              ? otherProfileImageUrl.startsWith('http')
                ? otherProfileImageUrl
                : getFullUrl(otherProfileImageUrl)
              : null
          }
          sx={{ width: 40, height: 40 }}
        />
        <Box flex={1}>
          <Typography variant="body1" fontWeight={600} sx={{ fontSize: "0.9375rem" }}>
            {otherStudentName || "Friend"}
          </Typography>
        </Box>
        <IconButton onClick={() => loadChatHistory(1, false)} size="small">
          <RefreshIcon />
        </IconButton>
      </Paper>

      {/* Messages Area */}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
          bgcolor: alpha(theme.palette.background.default, 0.5),
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : chatHistory.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">No messages yet. Start the conversation!</Typography>
          </Box>
        ) : (
          <>
            {chatHistory.map((msg, index) => {
              const isCurrentUser = msg.fromStudentId === currentStudentId;
              const showDateHeader =
                index === 0 || !isSameDay(msg.sentDateTime, chatHistory[index - 1].sentDateTime);
              const prevMessage = index > 0 ? chatHistory[index - 1] : null;
              const showAvatar = !prevMessage || prevMessage.fromStudentId !== msg.fromStudentId || !isSameDay(prevMessage.sentDateTime, msg.sentDateTime);

              return (
                <React.Fragment key={msg.messageId}>
                  {showDateHeader && (
                    <Box textAlign="center" my={2}>
                      <Chip
                        label={formatDateHeader(msg.sentDateTime)}
                        size="small"
                        sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                      />
                    </Box>
                  )}
                  <Box
                    display="flex"
                    justifyContent={isCurrentUser ? "flex-end" : "flex-start"}
                    mb={1}
                    sx={{
                      position: "relative",
                      "&:hover .message-menu": {
                        opacity: 1,
                        visibility: "visible",
                      },
                    }}
                  >
                    {!isCurrentUser && showAvatar && (
                      <Avatar
                        src={
                          msg.fromProfileImageUrl
                            ? msg.fromProfileImageUrl.startsWith('http')
                              ? msg.fromProfileImageUrl
                              : getFullUrl(msg.fromProfileImageUrl)
                            : null
                        }
                        sx={{ width: 32, height: 32, mr: 1, alignSelf: "flex-end" }}
                      />
                    )}
                    <Box
                      sx={{
                        maxWidth: isMobile ? "75%" : "60%",
                        position: "relative",
                      }}
                    >
                      {/* Reply preview */}
                      {msg.replyToMessage && (
                        <Box
                          sx={{
                            bgcolor: alpha(theme.palette.text.primary, 0.1),
                            p: 1,
                            mb: 0.5,
                            borderRadius: 1,
                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                          }}
                        >
                          <Typography variant="caption" fontWeight={600} display="block" sx={{ fontSize: "0.75rem" }}>
                            {msg.replyToMessage.fromStudentId === currentStudentId ? "You" : otherStudentName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: "0.7rem" }}>
                            {msg.replyToMessage.message || (msg.replyToMessage.imageUrl ? "📷 Image" : msg.replyToMessage.videoUrl ? "🎥 Video" : msg.replyToMessage.audioUrl ? "🎤 Audio" : "")}
                          </Typography>
                        </Box>
                      )}

                      {/* Message bubble */}
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          bgcolor: isCurrentUser ? "success.main" : "background.paper",
                          color: isCurrentUser ? "success.contrastText" : "text.primary",
                          borderRadius: 2,
                          position: "relative",
                        }}
                      >
                        {/* Message content */}
                        {msg.message && (
                          <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap", fontSize: "0.8125rem" }}>
                            {msg.message}
                          </Typography>
                        )}

                        {/* Image */}
                        {msg.imageUrl && (
                          <Box mt={msg.message ? 1 : 0}>
                            <img
                              src={
                                msg.imageUrl.startsWith('http')
                                  ? msg.imageUrl
                                  : getFullUrl(msg.imageUrl)
                              }
                              alt="Shared"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "300px",
                                borderRadius: 8,
                                cursor: "pointer",
                              }}
                              onClick={() => window.open(
                                msg.imageUrl.startsWith('http')
                                  ? msg.imageUrl
                                  : getFullUrl(msg.imageUrl),
                                '_blank'
                              )}
                            />
                          </Box>
                        )}

                        {/* Video */}
                        {msg.videoUrl && (
                          <Box mt={msg.message ? 1 : 0}>
                            <video
                              controls
                              style={{
                                maxWidth: "100%",
                                maxHeight: "300px",
                                borderRadius: 8,
                              }}
                            >
                              <source
                                src={
                                  msg.videoUrl.startsWith('http')
                                    ? msg.videoUrl
                                    : getFullUrl(msg.videoUrl)
                                }
                              />
                            </video>
                          </Box>
                        )}

                        {/* Audio */}
                        {msg.audioUrl && (
                          <Box mt={msg.message ? 1 : 0} display="flex" alignItems="center" gap={1}>
                            <audio
                              ref={(el) => (audioRefs.current[msg.messageId] = el)}
                              src={
                                msg.audioUrl.startsWith('http')
                                  ? msg.audioUrl
                                  : getFullUrl(msg.audioUrl)
                              }
                              style={{ display: "none" }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleToggleAudio(msg.messageId, msg.audioUrl)}
                              sx={{ color: isCurrentUser ? "success.contrastText" : "primary.main" }}
                            >
                              {playingAudioId === msg.messageId ? <PauseIcon /> : <PlayArrowIcon />}
                            </IconButton>
                            <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>Voice message</Typography>
                          </Box>
                        )}

                        {/* Message metadata */}
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} mt={0.5}>
                          <Typography variant="caption" sx={{ opacity: 0.7, fontSize: "0.7rem" }}>
                            {formatTime(msg.sentDateTime)}
                          </Typography>
                          {msg.isEdited && (
                            <Typography variant="caption" sx={{ opacity: 0.7, fontSize: "0.7rem", fontStyle: "italic" }}>
                              (edited)
                            </Typography>
                          )}
                          {getTickIcon(msg)}
                        </Box>
                      </Paper>

                      {/* Message menu */}
                      {isCurrentUser && (
                        <IconButton
                          className="message-menu"
                          size="small"
                          onClick={(e) => handleMenuOpen(e, msg)}
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: isCurrentUser ? -30 : -30,
                            opacity: 0,
                            visibility: "hidden",
                            transition: "opacity 0.2s, visibility 0.2s",
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}

                      {/* Reply button for other user's messages */}
                      {!isCurrentUser && (
                        <IconButton
                          size="small"
                          onClick={() => handleReplyClick(msg)}
                          sx={{ mt: 0.5, ml: 1 }}
                        >
                          <ReplyIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </React.Fragment>
              );
            })}

            {/* Typing indicator */}
            {isTyping && (
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Avatar
                  src={
                    otherProfileImageUrl
                      ? otherProfileImageUrl.startsWith('http')
                        ? otherProfileImageUrl
                        : getFullUrl(otherProfileImageUrl)
                      : null
                  }
                  sx={{ width: 32, height: 32 }}
                />
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: "background.paper",
                    borderRadius: 2,
                  }}
                >
                  <Box display="flex" gap={0.5}>
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "text.secondary",
                          animation: `${typingAnimation} 1.4s infinite`,
                          animationDelay: `${i * 0.2}s`,
                        }}
                      />
                    ))}
                  </Box>
                </Paper>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Reply preview */}
      {replyToMessage && (
        <Paper
          elevation={0}
          sx={{
            p: 1,
            mx: 2,
            mb: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box flex={1}>
            <Typography variant="caption" fontWeight={600} display="block" sx={{ fontSize: "0.75rem" }}>
              Replying to {replyToMessage.fromStudentId === currentStudentId ? "yourself" : otherStudentName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: "0.7rem" }}>
              {replyToMessage.message || (replyToMessage.imageUrl ? "📷 Image" : replyToMessage.videoUrl ? "🎥 Video" : replyToMessage.audioUrl ? "🎤 Audio" : "")}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setReplyToMessage(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      )}

      {/* Image/Video preview */}
      {(imagePreview || videoPreview) && (
        <Box p={2} borderTop={`1px solid ${theme.palette.divider}`}>
          <Box position="relative" display="inline-block">
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                style={{
                  maxWidth: "200px",
                  maxHeight: "200px",
                  borderRadius: 8,
                }}
              />
            )}
            {videoPreview && (
              <video
                src={videoPreview}
                controls
                style={{
                  maxWidth: "200px",
                  maxHeight: "200px",
                  borderRadius: 8,
                }}
              />
            )}
            <IconButton
              size="small"
              onClick={handleRemoveImage}
              sx={{
                position: "absolute",
                top: -8,
                right: -8,
                bgcolor: "error.main",
                color: "white",
                "&:hover": { bgcolor: "error.dark" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Recording indicator */}
      {isRecording && (
        <Box
          sx={{
            mb: 1,
            p: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "error.light",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "error.main",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <FiberManualRecordIcon
              sx={{
                color: "error.main",
                fontSize: 16,
                animation: "pulse 1.5s infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.3 },
                },
              }}
            />
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
              Recording: {formatRecordingTime(recordingTime)}
            </Typography>
          </Box>
          <Box display="flex" gap={0.5}>
            <IconButton
              size="small"
              onClick={handleStopRecording}
              sx={{
                bgcolor: "error.main",
                color: "white",
                "&:hover": { bgcolor: "error.dark" },
              }}
            >
              <StopIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleCancelRecording}
              sx={{
                color: "text.secondary",
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Audio Preview */}
      {audioPreview && audioPreviewBlob && (
        <Box
          sx={{
            mb: 1,
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "background.default",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <IconButton
            size="small"
            onClick={() => {
              if (audioRefs.current["preview"]) {
                audioRefs.current["preview"].pause();
                audioRefs.current["preview"] = null;
              }
              setAudioPreview(null);
              setAudioPreviewBlob(null);
            }}
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <audio
            ref={(el) => {
              if (el) audioRefs.current["preview"] = el;
            }}
            src={audioPreview}
            controls
            style={{ flex: 1, height: 32 }}
          />
        </Box>
      )}

      {/* Input Area */}
      <Paper
        elevation={2}
        sx={{
          p: 1.5,
          borderRadius: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box display="flex" alignItems="flex-end" gap={1}>
          {/* Attachment buttons */}
          <Box display="flex" gap={0.5}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleImageSelect}
              style={{ display: "none" }}
            />
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              onChange={handleImageSelect}
              style={{ display: "none" }}
            />
            <Tooltip title="Image">
              <IconButton
                size="small"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || uploadingImage || uploadingVideo || isRecording}
              >
                <ImageIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Camera">
              <IconButton
                size="small"
                onClick={handleOpenCamera}
                disabled={sending || uploadingImage || uploadingVideo || isRecording}
              >
                <CameraAltIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Video">
              <IconButton
                size="small"
                onClick={() => videoInputRef.current?.click()}
                disabled={sending || uploadingImage || uploadingVideo || isRecording}
              >
                <VideocamIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Voice">
              <IconButton
                size="small"
                onClick={isRecording ? handleStopRecording : handleStartRecording}
                disabled={sending || uploadingImage || uploadingVideo}
                color={isRecording ? "error" : "default"}
              >
                {isRecording ? <StopIcon /> : <MicIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Emoji">
              <IconButton
                size="small"
                onClick={(e) => setEmojiPickerAnchor(e.currentTarget)}
                disabled={sending || uploadingImage || uploadingVideo || isRecording}
              >
                <EmojiEmotionsIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Message input */}
          <TextField
            ref={inputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type a message..."
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              handleTyping();
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            disabled={sending || uploadingImage || uploadingVideo || isRecording}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                "&:hover fieldset": {
                  borderColor: "success.main",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "success.main",
                },
              },
            }}
          />

          {/* Send button */}
          <IconButton
            color="success"
            onClick={handleSendMessage}
            disabled={
              !isConnected ||
              sending ||
              uploadingImage ||
              uploadingVideo ||
              isRecording ||
              ((!message || message.trim().length === 0) && !selectedImage && !selectedVideo && !audioPreviewBlob)
            }
            sx={{
              bgcolor: "success.main",
              color: "white",
              "&:hover": {
                bgcolor: "success.dark",
                color: "white",
              },
              "&:disabled": {
                bgcolor: "action.disabledBackground",
                color: "action.disabled",
              },
              p: 1.25,
              "& svg": {
                color: "white !important",
              },
            }}
          >
            {sending || uploadingImage || uploadingVideo ? (
              <CircularProgress size={18} sx={{ color: "white !important" }} />
            ) : (
              <SendIcon sx={{ fontSize: 18, color: "white !important" }} />
            )}
          </IconButton>
        </Box>
      </Paper>

      {/* Camera Dialog */}
      <Dialog
        open={cameraOpen}
        onClose={handleCloseCamera}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "black",
            borderRadius: 2,
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: "relative", bgcolor: "black" }}>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              paddingTop: "75%", // 4:3 aspect ratio
              bgcolor: "black",
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              onLoadedMetadata={(e) => {
                const video = e.target;
                if (video && video.videoWidth > 0 && video.videoHeight > 0) {
                  console.log("Video metadata loaded:", { width: video.videoWidth, height: video.videoHeight });
                  setVideoReady(true);
                }
              }}
              onCanPlay={(e) => {
                const video = e.target;
                if (video && video.videoWidth > 0 && video.videoHeight > 0) {
                  console.log("Video can play:", { width: video.videoWidth, height: video.videoHeight });
                  setVideoReady(true);
                }
              }}
              onPlaying={() => {
                console.log("Video is playing");
                if (videoRef.current && videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
                  setVideoReady(true);
                }
              }}
              onError={(e) => {
                console.error("Video error:", e);
                try {
                  safeToast.error("Video playback error");
                } catch (toastErr) {
                  console.error("Toast error:", toastErr);
                }
              }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
            <canvas ref={canvasRef} style={{ display: "none" }} />
            
            {/* Camera Controls */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                p: 2,
                bgcolor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <IconButton
                onClick={handleCloseCamera}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.3)" },
                }}
              >
                <CloseIcon />
              </IconButton>
              
              <IconButton
                onClick={handleCapturePhoto}
                disabled={!videoReady}
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: videoReady ? "white" : "grey.400",
                  border: "4px solid",
                  borderColor: videoReady ? "grey.300" : "grey.500",
                  "&:hover": { bgcolor: videoReady ? "grey.100" : "grey.400" },
                  "&:disabled": {
                    bgcolor: "grey.400",
                    borderColor: "grey.500",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    bgcolor: videoReady ? "white" : "grey.500",
                    border: "2px solid",
                    borderColor: videoReady ? "grey.400" : "grey.600",
                  }}
                />
              </IconButton>
              
              <IconButton
                onClick={handleSwitchCamera}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.3)" },
                }}
                title="Switch camera"
              >
                <CameraAltIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Emoji Picker */}
      <Popover
        open={Boolean(emojiPickerAnchor)}
        anchorEl={emojiPickerAnchor}
        onClose={() => setEmojiPickerAnchor(null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Picker
          data={data}
          onEmojiSelect={handleEmojiSelect}
          theme="light"
        />
      </Popover>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Edit Message</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={editMessageText}
            onChange={(e) => setEditMessageText(e.target.value)}
            placeholder="Edit your message..."
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setEditDialogOpen(false)} color="secondary">
            Cancel
          </MDButton>
          <MDButton
            onClick={handleConfirmEdit}
            color="success"
            variant="contained"
            disabled={!editMessageText.trim() || editing}
          >
            {editing ? <CircularProgress size={20} /> : "Save"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this message? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDeleteDialogOpen(false)} color="secondary">
            Cancel
          </MDButton>
          <MDButton
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : "Delete"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Message Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {selectedMessage && !selectedMessage.imageUrl && !selectedMessage.videoUrl && !selectedMessage.audioUrl && (
          <MenuItem onClick={handleEditClick}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

PrivateChat.propTypes = {
  otherStudentId: PropTypes.number.isRequired,
  otherStudentName: PropTypes.string.isRequired,
  otherProfileImageUrl: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default PrivateChat;

